import { useQuery } from "@tanstack/react-query";
import JobPostFieldsService from "../../services/JobPostFieldsService";

// API response types (snake_case from API)
interface ApiJobPostField {
  visible: boolean;
  options?: string[];
  default_option?: string;
  type_label?: string;
  median_value?: any;
}

interface ApiJobPostFieldsData {
  [fieldName: string]: ApiJobPostField;
}

interface ApiJobPostFieldsResponse {
  status: boolean;
  data: {
    job_post_fields: ApiJobPostFieldsData;
  };
}

// Client-side types (camelCase for our application)
interface JobPostField {
  fieldName: string;
  visible: boolean;
  options?: string[];
  defaultOption?: string;
  labelType?: string;
  medianValue?: any;
}

interface JobPostFieldsData {
  [fieldName: string]: JobPostField;
}

// Mapper functions to transform API response to client-side format
const mapJobPostField = (
  fieldName: string,
  apiField: ApiJobPostField
): JobPostField => ({
  fieldName,
  visible: apiField.visible,
  options: apiField.options,
  defaultOption: apiField.default_option,
  labelType: apiField.type_label,
  medianValue: apiField.median_value
});

const mapJobPostFieldsData = (
  apiData: ApiJobPostFieldsData
): JobPostFieldsData => {
  const mappedData: JobPostFieldsData = {};

  Object.entries(apiData).forEach(([fieldName, apiField]) => {
    mappedData[fieldName] = mapJobPostField(fieldName, apiField);
  });

  return mappedData;
};

// Query key factory for job post fields
export const jobPostFieldsKeys = {
  all: ["jobPostFields"] as const,
  list: () => [...jobPostFieldsKeys.all, "list"] as const,
  byCategory: (categoryId?: number) =>
    [...jobPostFieldsKeys.list(), { categoryId }] as const
};

/**
 * React Query hook to fetch job post fields
 * Uses React Query's built-in caching mechanism with select function for data transformation
 *
 * @param categoryId - Optional category ID to filter fields
 * @param options - React Query options
 * @returns Query result with job post fields data
 */
export const useJobPostFieldsQuery = <TData = JobPostFieldsData>(
  categoryId?: number,
  options: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    select?: (data: ApiJobPostFieldsData) => TData;
  } = {}
) => {
  const { select, ...queryOptions } = options;

  return useQuery({
    queryKey: jobPostFieldsKeys.byCategory(categoryId),
    queryFn: async (): Promise<ApiJobPostFieldsData> => {
      try {
        const response = await JobPostFieldsService.getJobPostFields(
          categoryId
        );

        // Handle potential undefined response
        if (!response || !response.data) {
          throw new Error("No data received from job post fields API");
        }

        const { data } = response.data;

        // Return raw API data - transformation will happen in select function
        return data.job_post_fields || {};
      } catch (error) {
        console.error("Error fetching job post fields:", error);
        throw error;
      }
    },
    // Transform API response from snake_case to camelCase using select function
    select:
      select || (mapJobPostFieldsData as (data: ApiJobPostFieldsData) => TData),
    staleTime: 1000 * 60 * 60 * 2, // 2 hours - fields don't change frequently
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions
  });
};

/**
 * Hook to get job post fields data with loading and error states
 * Provides a simplified interface similar to Redux selectors
 */
export const useJobPostFields = ({
  categoryId,
  enabled = true
}: {
  categoryId?: number;
  enabled?: boolean;
}) => {
  const { data, isLoading, error, isError, isFetching } = useJobPostFieldsQuery(
    categoryId,
    { enabled }
  );

  return {
    jobPostFields: data || {},
    isLoading,
    isFetching, // Useful for showing loading state during refetch
    error: isError ? error : null,
    hasData: !!data && Object.keys(data).length > 0,
    hasCategory: categoryId !== undefined && categoryId !== null,
    fieldNames: data ? Object.keys(data) : []
  };
};

// Export types for use in other components
export type {
  // Client-side types (camelCase)
  JobPostField,
  JobPostFieldsData,
  // API types (snake_case) - exported for reference but prefer using client-side types
  ApiJobPostField,
  ApiJobPostFieldsData,
  ApiJobPostFieldsResponse
};
